/**
 * AI Interview Practice System - Feedback Modal Component
 *
 * Copyright (c) 2024 Technical Interviewer Attempt. All Rights Reserved.
 * Proprietary and Confidential. Unauthorized copying, distribution, or use is prohibited.
 */

import React from 'react';

interface FeedbackStage {
  type?: string;
  name?: string;
  score?: number | null;
  summary?: string;
  improvements?: string[];
  strengths?: string[];
  speaking_clarity_analysis?: string;
  problem_solving_approach_score?: number | null;
}

interface Feedback {
  score?: number | null;
  summary?: string;
  improvements?: string[];
  strengths?: string[];
  speaking_clarity_analysis?: string;
  problem_solving_approach_score?: number | null;
  overall_score?: number;
  overall_assessment?: string;
  isSessionSummary?: boolean;
  totalScore?: number | null;
  stages?: FeedbackStage[];
  stageName?: string; // For individual stage feedback in multi-session
}

interface Message {
  sender: 'user' | 'ai' | 'system' | 'error';
  text: string;
  timestamp: Date | string;
  isStreaming?: boolean;
}

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  feedback: Feedback | null;
  messages?: Message[];
  hideExport?: boolean;
  footer?: React.ReactNode;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  feedback,
  messages = [],
  hideExport = false,
  footer,
}) => {
  const [showTranscript, setShowTranscript] = React.useState(false);

  if (!isOpen) return null;

  const formatTimestamp = (timestamp: Date | string): string => {
    if (!timestamp) return '';
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const getSenderLabel = (sender: string): string => {
    switch (sender) {
      case 'user': return 'You';
      case 'ai': return 'AI Interviewer';
      case 'system': return 'System';
      case 'error': return 'Error';
      default: return sender;
    }
  };

  const getSenderStyle = (sender: string): { bg: string; text: string; border: string } => {
    switch (sender) {
      case 'user':
        return { bg: 'bg-indigo-50', text: 'text-indigo-900', border: 'border-indigo-200' };
      case 'ai':
        return { bg: 'bg-slate-50', text: 'text-slate-900', border: 'border-slate-200' };
      case 'system':
        return { bg: 'bg-amber-50', text: 'text-amber-900', border: 'border-amber-200' };
      case 'error':
        return { bg: 'bg-red-50', text: 'text-red-900', border: 'border-red-200' };
      default:
        return { bg: 'bg-gray-50', text: 'text-gray-900', border: 'border-gray-200' };
    }
  };

  // Export transcript function
  const exportTranscript = (format: 'markdown' | 'json' | 'txt') => {
    if (!messages || messages.length === 0) {
      alert('No transcript available to export.');
      return;
    }

    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    let content = '';
    let filename = '';
    let mimeType = '';

    if (format === 'markdown') {
      filename = `interview_feedback_${dateStr}.md`;
      mimeType = 'text/markdown';
      
      content = `# Interview Feedback\n\n`;
      content += `**Date:** ${now.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}\n\n`;
      
      if (feedback) {
        content += `## Feedback Summary\n\n`;
        if (feedback.score !== null && feedback.score !== undefined) {
          content += `**Score:** ${feedback.score}/100\n\n`;
        }
        if (feedback.summary) {
          content += `**Summary:** ${feedback.summary}\n\n`;
        }
        if (feedback.speaking_clarity_analysis) {
          content += `**Speaking clarity:** ${feedback.speaking_clarity_analysis}\n\n`;
        }
        if (feedback.problem_solving_approach_score !== null && feedback.problem_solving_approach_score !== undefined) {
          content += `**Problem-solving approach score:** ${feedback.problem_solving_approach_score}/100\n\n`;
        }
        if (feedback.strengths && feedback.strengths.length > 0) {
          content += `### Strengths\n`;
          feedback.strengths.forEach((s) => {
            content += `- ✓ ${s}\n`;
          });
          content += `\n`;
        }
        if (feedback.improvements && feedback.improvements.length > 0) {
          content += `### Areas for Improvement\n`;
          feedback.improvements.forEach((i) => {
            content += `- • ${i}\n`;
          });
          content += `\n`;
        }
      }

      content += `---\n\n## Conversation Transcript\n\n`;
      messages.forEach((msg) => {
        const senderLabel = msg.sender === 'user' ? '**You:**' : msg.sender === 'ai' ? '**AI Interviewer:**' : msg.sender === 'system' ? '**System:**' : '**Error:**';
        const timestamp = formatTimestamp(msg.timestamp);
        content += `${senderLabel} (${timestamp})\n${msg.text}\n\n`;
      });
    } else if (format === 'json') {
      filename = `interview_feedback_${dateStr}.json`;
      mimeType = 'application/json';
      content = JSON.stringify({
        date: now.toISOString(),
        feedback: feedback ? {
          score: feedback.score,
          summary: feedback.summary,
          speaking_clarity_analysis: feedback.speaking_clarity_analysis,
          problem_solving_approach_score: feedback.problem_solving_approach_score,
          strengths: feedback.strengths,
          improvements: feedback.improvements,
        } : null,
        messages: messages.map((msg) => ({
          sender: msg.sender,
          text: msg.text,
          timestamp: msg.timestamp instanceof Date ? msg.timestamp.toISOString() : msg.timestamp,
        })),
      }, null, 2);
    } else if (format === 'txt') {
      filename = `interview_feedback_${dateStr}.txt`;
      mimeType = 'text/plain';
      
      content = `Interview Feedback\n`;
      content += `==================\n\n`;
      content += `Date: ${now.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}\n\n`;
      
      if (feedback) {
        content += `FEEDBACK SUMMARY\n`;
        content += `----------------\n`;
        if (feedback.score !== null && feedback.score !== undefined) {
          content += `Score: ${feedback.score}/100\n`;
        }
        if (feedback.summary) {
          content += `Summary: ${feedback.summary}\n`;
        }
        if (feedback.speaking_clarity_analysis) {
          content += `Speaking clarity: ${feedback.speaking_clarity_analysis}\n`;
        }
        if (feedback.problem_solving_approach_score !== null && feedback.problem_solving_approach_score !== undefined) {
          content += `Problem-solving approach score: ${feedback.problem_solving_approach_score}/100\n`;
        }
        if (feedback.strengths && feedback.strengths.length > 0) {
          content += `\nStrengths:\n`;
          feedback.strengths.forEach((s) => {
            content += `  ✓ ${s}\n`;
          });
        }
        if (feedback.improvements && feedback.improvements.length > 0) {
          content += `\nAreas for Improvement:\n`;
          feedback.improvements.forEach((i) => {
            content += `  • ${i}\n`;
          });
        }
        content += `\n`;
      }

      content += `${'='.repeat(50)}\n\n`;
      content += `CONVERSATION TRANSCRIPT\n\n`;
      messages.forEach((msg) => {
        const senderLabel = msg.sender === 'user' ? '[YOU]' : msg.sender === 'ai' ? '[AI]' : msg.sender === 'system' ? '[SYSTEM]' : '[ERROR]';
        const timestamp = formatTimestamp(msg.timestamp);
        content += `${senderLabel} (${timestamp}): ${msg.text}\n\n`;
      });
    }

    // Create and download file
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getScoreColor = (score: number | null | undefined): string => {
    if (score === null || score === undefined) return '#94a3b8'; // gray for no score
    if (score >= 80) return '#10b981'; // green
    if (score >= 60) return '#f59e0b'; // amber
    return '#ef4444'; // red
  };

  const getScoreLabel = (score: number | null | undefined): string => {
    if (score === null || score === undefined) return 'N/A';
    if (score >= 90) return 'Excellent';
    if (score >= 80) return 'Very Good';
    if (score >= 70) return 'Good';
    if (score >= 60) return 'Fair';
    return 'Needs Improvement';
  };

  const getInterviewIcon = (type?: string): string => {
    const icons: Record<string, string> = {
      system_design: '🏗️',
      coding: '💻',
      technical: '🎯',
      cv_review: '📄',
      architecture_review: '📐',
      behavioural: '🗣️',
    };
    return icons[type || ''] || '📝';
  };

  // Check if this is a session summary (multiple stages)
  const isSessionSummary = feedback?.isSessionSummary;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10000] p-3 sm:p-5 animate-fade-in"
      onClick={onClose}
    >
      <div
        className={`bg-white rounded-xl sm:rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-slide-up ${
          isSessionSummary ? 'max-w-5xl' : ''
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-4 sm:p-6 pb-4 sm:pb-6 border-b-2 border-slate-200 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-t-xl sm:rounded-t-2xl">
          <h2 className="text-lg sm:text-2xl font-semibold m-0">
            {isSessionSummary ? '🎉 Session Complete!' : 'Interview Feedback'}
          </h2>
          <div className="flex items-center gap-2">
            {/* Export Button with Dropdown */}
            {!hideExport && messages && messages.length > 0 && (
              <div className="relative group">
                <button
                  className="bg-white/20 hover:bg-white/30 border-none text-white text-sm px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-all duration-200"
                  onClick={(e) => {
                    e.stopPropagation();
                    const menu = document.getElementById('feedback-export-menu');
                    if (menu) menu.classList.toggle('hidden');
                  }}
                >
                  📥 Export
                  <span className="text-xs">▼</span>
                </button>
                <div
                  id="feedback-export-menu"
                  className="hidden absolute right-0 mt-2 w-44 bg-white rounded-lg shadow-lg border border-gray-200 z-10 py-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => {
                      exportTranscript('markdown');
                      const menu = document.getElementById('feedback-export-menu');
                      if (menu) menu.classList.add('hidden');
                    }}
                  >
                    📄 Export as Markdown
                  </button>
                  <button
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => {
                      exportTranscript('json');
                      const menu = document.getElementById('feedback-export-menu');
                      if (menu) menu.classList.add('hidden');
                    }}
                  >
                    📋 Export as JSON
                  </button>
                  <button
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => {
                      exportTranscript('txt');
                      const menu = document.getElementById('feedback-export-menu');
                      if (menu) menu.classList.add('hidden');
                    }}
                  >
                    📝 Export as Text
                  </button>
                </div>
              </div>
            )}
            <button
              className="bg-white/20 hover:bg-white/30 border-none text-white text-2xl sm:text-3xl w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition-all duration-200 leading-none p-0 hover:scale-110"
              onClick={onClose}
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6 lg:p-8">
          {feedback ? (
            <>
              {isSessionSummary ? (
                /* Session Summary View */
                <>
                  {feedback.totalScore !== null &&
                    feedback.totalScore !== undefined && (
                      <div className="flex justify-center mb-6 sm:mb-8">
                        <div
                          className="w-[100px] h-[100px] sm:w-[130px] sm:h-[130px] rounded-full border-4 sm:border-[6px] flex flex-col items-center justify-center bg-slate-50"
                          style={{
                            borderColor: getScoreColor(feedback.totalScore),
                          }}
                        >
                          <div
                            className="text-4xl sm:text-5xl font-bold leading-none mb-1"
                            style={{
                              color: getScoreColor(feedback.totalScore),
                            }}
                          >
                            {feedback.totalScore}
                          </div>
                          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                            Overall Score
                          </div>
                        </div>
                      </div>
                    )}

                  <div className="mb-6 sm:mb-7">
                    <h3 className="text-slate-800 text-lg sm:text-xl font-semibold mb-3 sm:mb-4 m-0">
                      Interview Stage Results
                    </h3>
                    <div className="flex flex-col gap-2 sm:gap-3">
                      {feedback.stages?.map((stage, index) => (
                        <div
                          key={index}
                          className="bg-slate-50 rounded-xl p-3 sm:p-4 border border-slate-200 transition-all duration-200 hover:border-slate-300 hover:shadow-md"
                        >
                          <div className="flex flex-wrap items-center gap-2 sm:gap-2.5 mb-2">
                            <span className="text-lg sm:text-xl">
                              {getInterviewIcon(stage.type)}
                            </span>
                            <span className="text-slate-800 font-medium flex-1 text-sm sm:text-base">
                              {stage.name}
                            </span>
                            {stage.score !== null &&
                              stage.score !== undefined && (
                                <span
                                  className="font-bold text-base sm:text-lg whitespace-nowrap"
                                  style={{ color: getScoreColor(stage.score) }}
                                >
                                  {stage.score}/100
                                </span>
                              )}
                          </div>
                          {stage.summary && (
                            <p className="text-slate-600 text-xs sm:text-sm leading-relaxed m-0 mb-2">
                              {stage.summary}
                            </p>
                          )}
                          {stage.speaking_clarity_analysis && (
                            <p className="text-slate-600 text-xs sm:text-sm leading-relaxed m-0 mb-2">
                              <span className="font-semibold text-slate-700">Speaking clarity:</span> {stage.speaking_clarity_analysis}
                            </p>
                          )}
                          {(stage.problem_solving_approach_score !== null && stage.problem_solving_approach_score !== undefined) && (
                            <p className="text-slate-600 text-xs sm:text-sm m-0 mb-2">
                              <span className="font-semibold text-slate-700">Problem-solving:</span> {stage.problem_solving_approach_score}/100
                            </p>
                          )}
                          {stage.strengths && stage.strengths.length > 0 && (
                            <div className="mt-2 mb-2">
                              <h4 className="text-slate-700 text-xs sm:text-sm font-semibold mb-1 m-0">
                                Strengths:
                              </h4>
                              <ul className="list-none m-0 p-0 space-y-1">
                                {stage.strengths.map((strength, idx) => (
                                  <li
                                    key={idx}
                                    className="text-slate-600 text-xs sm:text-sm flex items-start gap-1.5"
                                  >
                                    <span className="text-green-500 mt-0.5">
                                      ✓
                                    </span>
                                    <span>{strength}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {stage.improvements &&
                            stage.improvements.length > 0 && (
                              <div className="mt-2">
                                <h4 className="text-slate-700 text-xs sm:text-sm font-semibold mb-1 m-0">
                                  Areas for Improvement:
                                </h4>
                                <ul className="list-none m-0 p-0 space-y-1">
                                  {stage.improvements.map(
                                    (improvement, idx) => (
                                      <li
                                        key={idx}
                                        className="text-slate-600 text-xs sm:text-sm flex items-start gap-1.5"
                                      >
                                        <span className="text-red-500 mt-0.5">
                                          •
                                        </span>
                                        <span>{improvement}</span>
                                      </li>
                                    )
                                  )}
                                </ul>
                              </div>
                            )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="text-center p-5 bg-gradient-to-r from-indigo-500/10 to-purple-600/10 rounded-xl mt-5">
                    <p className="text-slate-800 text-lg font-medium m-0">
                      Great job completing your interview session! 🎉
                    </p>
                    <p className="text-slate-500 text-sm mt-2 mb-0 font-normal">
                      Review your results and keep practicing to improve!
                    </p>
                  </div>
                </>
              ) : (
                /* Single Interview Feedback View or Stage Feedback */
                <>
                  {feedback.stageName && (
                    <div className="mb-4 text-center">
                      <h3 className="text-slate-700 text-lg font-semibold m-0">
                        {feedback.stageName} - Stage Feedback
                      </h3>
                    </div>
                  )}
                  {feedback.score !== null && feedback.score !== undefined && (
                    <div className="flex justify-center mb-6 sm:mb-8">
                      <div
                        className="w-[120px] h-[120px] sm:w-[150px] sm:h-[150px] rounded-full border-4 sm:border-[6px] flex flex-col items-center justify-center bg-slate-50 px-2"
                        style={{
                          borderColor: getScoreColor(feedback.score ?? 0),
                        }}
                      >
                        <div
                          className="text-5xl sm:text-6xl font-bold leading-none mb-0.5 sm:mb-1"
                          style={{ color: getScoreColor(feedback.score ?? 0) }}
                        >
                          {feedback.score}
                        </div>
                        <div className="text-[9px] sm:text-[10px] font-semibold text-slate-500 uppercase tracking-wide text-center leading-tight px-1">
                          {getScoreLabel(feedback.score ?? 0)}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mb-5 sm:mb-7">
                    <h3 className="text-slate-800 text-lg sm:text-xl font-semibold mb-2 sm:mb-3 m-0">
                      Summary
                    </h3>
                    <p className="leading-relaxed text-slate-600 text-sm sm:text-base m-0">
                      {feedback.summary}
                    </p>
                  </div>

                  {feedback.speaking_clarity_analysis && (
                    <div className="mb-5 sm:mb-7">
                      <h3 className="text-slate-800 text-lg sm:text-xl font-semibold mb-2 sm:mb-3 m-0">
                        Speaking clarity
                      </h3>
                      <p className="leading-relaxed text-slate-600 text-sm sm:text-base m-0">
                        {feedback.speaking_clarity_analysis}
                      </p>
                    </div>
                  )}

                  {(feedback.problem_solving_approach_score !== null && feedback.problem_solving_approach_score !== undefined) && (
                    <div className="mb-5 sm:mb-7">
                      <h3 className="text-slate-800 text-lg sm:text-xl font-semibold mb-2 sm:mb-3 m-0">
                        Problem-solving approach
                      </h3>
                      <div className="flex items-center gap-3">
                        <div
                          className="w-14 h-14 rounded-full border-2 flex items-center justify-center font-bold text-lg"
                          style={{ borderColor: getScoreColor(feedback.problem_solving_approach_score), color: getScoreColor(feedback.problem_solving_approach_score) }}
                        >
                          {feedback.problem_solving_approach_score}
                        </div>
                        <span className="text-slate-600 text-sm">
                          {getScoreLabel(feedback.problem_solving_approach_score)} / 100
                        </span>
                      </div>
                    </div>
                  )}

                  {feedback.improvements &&
                    feedback.improvements.length > 0 && (
                      <div className="mb-5 sm:mb-7">
                        <h3 className="text-slate-800 text-lg sm:text-xl font-semibold mb-2 sm:mb-3 m-0">
                          Areas for Improvement
                        </h3>
                        <ul className="list-none p-0 m-0">
                          {feedback.improvements.map((improvement, index) => (
                            <li
                              key={index}
                              className="px-3 sm:px-4 py-2 sm:py-3 mb-1 sm:mb-2 rounded-lg leading-relaxed text-red-900 bg-red-50 border-l-4 border-red-500 text-sm sm:text-base"
                            >
                              {improvement}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                  {feedback.strengths && feedback.strengths.length > 0 && (
                    <div className="mb-5 sm:mb-7">
                      <h3 className="text-slate-800 text-lg sm:text-xl font-semibold mb-2 sm:mb-3 m-0">
                        Strengths
                      </h3>
                      <ul className="list-none p-0 m-0">
                        {feedback.strengths.map((strength, index) => (
                          <li
                            key={index}
                            className="px-3 sm:px-4 py-2 sm:py-3 mb-1 sm:mb-2 rounded-lg leading-relaxed text-green-900 bg-green-50 border-l-4 border-green-500 text-sm sm:text-base"
                          >
                            {strength}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}

              {/* Interview Transcript Section */}
              {messages && messages.length > 0 && (
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <button
                    onClick={() => setShowTranscript(!showTranscript)}
                    className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors"
                  >
                    <span className="text-slate-800 font-semibold flex items-center gap-2">
                      📝 Interview Transcript
                      <span className="text-slate-500 font-normal text-sm">
                        ({messages.length} messages)
                      </span>
                    </span>
                    <span className={`text-slate-500 transition-transform duration-200 ${showTranscript ? 'rotate-180' : ''}`}>
                      ▼
                    </span>
                  </button>
                  
                  {showTranscript && (
                    <div className="mt-3 max-h-[400px] overflow-y-auto border border-slate-200 rounded-lg bg-white">
                      <div className="p-3 space-y-3">
                        {messages.map((msg, index) => {
                          const style = getSenderStyle(msg.sender);
                          return (
                            <div
                              key={index}
                              className={`p-3 rounded-lg border ${style.bg} ${style.border}`}
                            >
                              <div className="flex items-center justify-between mb-1">
                                <span className={`font-semibold text-sm ${style.text}`}>
                                  {getSenderLabel(msg.sender)}
                                </span>
                                {msg.timestamp && (
                                  <span className="text-xs text-slate-400">
                                    {formatTimestamp(msg.timestamp)}
                                  </span>
                                )}
                              </div>
                              <p className={`text-sm leading-relaxed m-0 ${style.text} whitespace-pre-wrap`}>
                                {msg.text}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {footer}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 px-5 text-slate-500">
              <div className="w-12 h-12 border-4 border-slate-200 border-t-indigo-500 rounded-full animate-spin mb-4"></div>
              <p className="text-base font-medium m-0">
                Generating feedback...
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal;
